/**
 * What the server renders before the app has loaded: a title, a description
 * and the brand's own contact and policy pages, in the page's language.
 *
 * `_app` renders a page only once its client-side language redirect is done,
 * so without this the server sent an empty `<div id="__next">` with no title.
 * That is what a scanner that does not run JavaScript sees, and Fortinet and
 * Webroot rated reservation.pinchonation.de as phishing (VirusTotal, October
 * 2026). The same page is served on every country's domain, so the brand
 * follows the language: Pinchos in Swedish, Pincho Nation otherwise, as on
 * www.pinchos.se and www.pinchonation.no/.dk/.fi/.de.
 */

export type ShellLink = { label: string; href: string };

export type SiteShell = {
  lang: string;
  brand: string;
  title: string;
  heading: string;
  description: string;
  loading: string;
  noscript: string;
  /** Where the site root sends a guest to choose a restaurant: the country site's booking page. */
  booking: ShellLink;
  links: ShellLink[];
};

const privacyPolicy = (locale: string) => `https://pages.pinchos.se/${locale}/privacy-policy`;

// policies.pinchos.se has no German cookie policy (404), so German links to the English one.
const cookiePolicy = (locale: string) =>
  `https://policies.pinchos.se/${locale}/policy-page/cookie-policy/latest`;

const SHELLS: Record<string, Omit<SiteShell, 'lang' | 'title'>> = {
  en: {
    brand: 'Pincho Nation',
    heading: 'Book a table',
    description: 'Book a table at Pincho Nation: tapas-sized dishes from all around the world.',
    loading: 'Loading the booking…',
    noscript: 'Turn on JavaScript in your browser to book a table.',
    booking: { label: 'Choose a restaurant', href: 'https://www.pinchos.se/booking' },
    links: [
      { label: 'Contact us', href: 'https://www.pinchos.se/contact' },
      { label: 'FAQ', href: 'https://www.pinchos.se/faq' },
      { label: 'Privacy policy', href: privacyPolicy('en') },
      { label: 'Cookie policy', href: cookiePolicy('en') },
    ],
  },
  sv: {
    brand: 'Pinchos',
    heading: 'Boka bord',
    description: 'Boka bord på Pinchos: smårätter från hela världen.',
    loading: 'Laddar bokningen…',
    noscript: 'Slå på JavaScript i webbläsaren för att boka bord.',
    booking: { label: 'Välj restaurang', href: 'https://www.pinchos.se/booking' },
    links: [
      { label: 'Kontakt', href: 'https://www.pinchos.se/contact' },
      { label: 'Vanliga frågor', href: 'https://www.pinchos.se/faq' },
      { label: 'Integritetspolicy', href: privacyPolicy('sv') },
      { label: 'Cookiepolicy', href: cookiePolicy('sv') },
    ],
  },
  da: {
    brand: 'Pincho Nation',
    heading: 'Book bord',
    description: 'Book bord hos Pincho Nation: små retter fra hele verden.',
    loading: 'Indlæser bookingen…',
    noscript: 'Slå JavaScript til i din browser for at booke bord.',
    booking: { label: 'Vælg restaurant', href: 'https://www.pinchonation.dk/booking' },
    links: [
      { label: 'Kontakt', href: 'https://www.pinchonation.dk/contact' },
      { label: 'Ofte stillede spørgsmål', href: 'https://www.pinchonation.dk/faq' },
      { label: 'Privatlivspolitik', href: privacyPolicy('da') },
      { label: 'Cookiepolitik', href: cookiePolicy('da') },
    ],
  },
  nb: {
    brand: 'Pincho Nation',
    heading: 'Bestill bord',
    description: 'Bestill bord hos Pincho Nation: småretter fra hele verden.',
    loading: 'Laster inn bestillingen…',
    noscript: 'Slå på JavaScript i nettleseren for å bestille bord.',
    booking: { label: 'Velg restaurant', href: 'https://www.pinchonation.no/booking' },
    links: [
      { label: 'Kontakt', href: 'https://www.pinchonation.no/contact' },
      { label: 'Ofte stilte spørsmål', href: 'https://www.pinchonation.no/faq' },
      { label: 'Personvernerklæring', href: privacyPolicy('nb') },
      { label: 'Informasjonskapsler', href: cookiePolicy('nb') },
    ],
  },
  fi: {
    brand: 'Pincho Nation',
    heading: 'Varaa pöytä',
    description: 'Pincho Nation: pieniä annoksia eri puolilta maailmaa. Varaa pöytä.',
    loading: 'Ladataan varausta…',
    noscript: 'Ota JavaScript käyttöön selaimessa varataksesi pöydän.',
    booking: { label: 'Valitse ravintola', href: 'https://www.pinchonation.fi/booking' },
    links: [
      { label: 'Yhteystiedot', href: 'https://www.pinchonation.fi/contact' },
      { label: 'Usein kysyttyä', href: 'https://www.pinchonation.fi/faq' },
      { label: 'Tietosuojaseloste', href: privacyPolicy('fi') },
      { label: 'Evästekäytäntö', href: cookiePolicy('fi') },
    ],
  },
  de: {
    brand: 'Pincho Nation',
    heading: 'Tisch reservieren',
    description: 'Tisch reservieren bei Pincho Nation: kleine Gerichte aus aller Welt.',
    loading: 'Reservierung wird geladen…',
    noscript: 'Aktiviere JavaScript in deinem Browser, um einen Tisch zu reservieren.',
    // www.pinchonation.de has no /booking; its restaurant list links each one's booking.
    booking: { label: 'Restaurant wählen', href: 'https://www.pinchonation.de/restaurants' },
    // www.pinchonation.de has no contact or FAQ page; its Impressum has the contact details.
    links: [
      { label: 'Impressum', href: 'https://www.pinchonation.de/impressum' },
      { label: 'Datenschutzerklärung', href: privacyPolicy('de') },
      { label: 'Cookie-Richtlinie', href: cookiePolicy('en') },
    ],
  },
};

/** The shell for a locale; English for one the app doesn't have. */
export function siteShell(locale?: string): SiteShell {
  const lang = locale && SHELLS[locale] ? locale : 'en';
  const shell = SHELLS[lang];
  return { ...shell, lang, title: `${shell.heading} – ${shell.brand}` };
}
